/**
 * Replay controls — the floating pill docked at the bottom of a chart that is
 * in replay. Step, play/pause, speed, jump to either end, exit.
 *
 * Mounted only while that tab's replay is active, so the play loop below
 * exists only when it can run.
 *
 * Note it reads `s.ohlc.byTab[tabId]` DIRECTLY rather than through
 * `useTabOhlc`: that hook truncates at the cursor, which is the whole point
 * everywhere else, but this bar is the one surface that has to know what lies
 * ahead — how many bars remain, and which bar to step onto next.
 *
 * See features/chart_replay.md.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useActiveTabId,
  useAppDispatch,
  useAppSelector,
  useTabReplay,
} from "@/state/hooks";
import {
  REPLAY_SPEEDS,
  exitReplay,
  setReplayCursor,
  setReplayPlaying,
  setReplaySpeed,
  type ReplaySpeed,
} from "@/state/slices/replaySlice";
import { advanceCursor, replayEndIndex } from "@/utils/replayCursor";
import { fmtLineLabel } from "@/utils/chartTime";
import styles from "./ReplayBar.module.css";

const StepBackIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
    <path d="M11.5 3.2v9.6a.5.5 0 0 1-.77.42L4.6 8.42a.5.5 0 0 1 0-.84l6.13-4.8a.5.5 0 0 1 .77.42Z" />
    <rect x="3" y="3" width="1.4" height="10" rx="0.7" />
  </svg>
);

const StepForwardIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
    <path d="M4.5 3.2v9.6a.5.5 0 0 0 .77.42l6.13-4.8a.5.5 0 0 0 0-.84l-6.13-4.8a.5.5 0 0 0-.77.42Z" />
    <rect x="11.6" y="3" width="1.4" height="10" rx="0.7" />
  </svg>
);

const PlayIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
    <path d="M4.5 2.6v10.8a.5.5 0 0 0 .76.43l9-5.4a.5.5 0 0 0 0-.86l-9-5.4a.5.5 0 0 0-.76.43Z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
    <rect x="4" y="3" width="3.2" height="10" rx="1" />
    <rect x="8.8" y="3" width="3.2" height="10" rx="1" />
  </svg>
);

const ToStartIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
    <path d="M12.5 3.6v8.8a.5.5 0 0 1-.78.41l-6.4-4.4a.5.5 0 0 1 0-.82l6.4-4.4a.5.5 0 0 1 .78.41Z" />
    <rect x="2.6" y="3" width="1.5" height="10" rx="0.75" />
  </svg>
);

const ToEndIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
    <path d="M3.5 3.6v8.8a.5.5 0 0 0 .78.41l6.4-4.4a.5.5 0 0 0 0-.82l-6.4-4.4a.5.5 0 0 0-.78.41Z" />
    <rect x="11.9" y="3" width="1.5" height="10" rx="0.75" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);

export function ReplayBar({ tabId }: { tabId: string }) {
  const dispatch = useAppDispatch();
  const { cursorTime, playing, speed } = useTabReplay(tabId);
  // Split view makes two panes "active" at once; only the focused one takes
  // the keyboard (see the key handler below).
  const hasKeyboard = useActiveTabId() === tabId;
  // The FULL series — see the note in the file header.
  const candles = useAppSelector(
    (s) => s.ohlc.byTab[tabId]?.candles ?? EMPTY,
  );

  const total = candles.length;
  const index = cursorTime == null ? 0 : replayEndIndex(candles, cursorTime);
  const atStart = index <= 1;
  const atEnd = total === 0 || index >= total;

  const step = useCallback(
    (delta: number) => {
      if (cursorTime == null) return;
      const move = advanceCursor(candles, cursorTime, delta);
      if (!move) return;
      if (move.moved) dispatch(setReplayCursor({ tabId, cursorTime: move.time }));
      // Caught up with the live edge — stop rather than spin against it.
      if (delta > 0 && move.atEnd) {
        dispatch(setReplayPlaying({ tabId, playing: false }));
      }
    },
    [candles, cursorTime, dispatch, tabId],
  );

  // Refs the play loop reads instead of re-subscribing: `speed` so changing
  // it doesn't restart the loop, `candles` so it never closes over a stale
  // array, and `cursorTime` so a manual step/jump made WHILE playing is
  // picked up on the next frame.
  const speedRef = useRef(speed);
  speedRef.current = speed;
  const candlesRef = useRef(candles);
  candlesRef.current = candles;
  const cursorRef = useRef(cursorTime);
  cursorRef.current = cursorTime;
  const playingRef = useRef(playing);
  playingRef.current = playing;

  // --- Press-and-hold on the step buttons ---
  //
  // A click steps one bar; holding keeps stepping, accelerating, until
  // release or an end of the data. Like the play loop, the repeat OWNS its
  // bar index rather than re-reading the cursor from Redux each tick — a
  // repeat faster than React re-renders would otherwise recompute from a
  // stale cursor and re-dispatch the same bar, which is the bug that made
  // every playback speed advance one bar at a time.
  const HOLD_DELAY_MS = 350; // press-and-hold threshold, not a click
  const holdRef = useRef<number | null>(null);

  const stopHold = useCallback(() => {
    if (holdRef.current != null) {
      window.clearTimeout(holdRef.current);
      holdRef.current = null;
    }
  }, []);

  const startHold = useCallback(
    (delta: number) => {
      stopHold();
      const bars = candlesRef.current;
      const from = cursorRef.current;
      if (bars.length === 0 || from == null) return;
      // Holding a step button takes over from playback rather than fighting
      // it for the cursor.
      if (playingRef.current) {
        dispatch(setReplayPlaying({ tabId, playing: false }));
      }
      let idx = Math.max(replayEndIndex(bars, from) - 1, 0);
      const last = bars.length - 1;
      let delay = 70;
      const advance = () => {
        const next = Math.min(Math.max(idx + delta, 0), last);
        if (next === idx) {
          stopHold(); // hit an end — stop rather than spin
          return false;
        }
        idx = next;
        dispatch(setReplayCursor({ tabId, cursorTime: bars[idx].time }));
        return true;
      };
      if (!advance()) return; // the click itself
      const repeat = () => {
        if (!advance()) return;
        delay = Math.max(25, delay - 4); // ramp up while held
        holdRef.current = window.setTimeout(repeat, delay);
      };
      holdRef.current = window.setTimeout(repeat, HOLD_DELAY_MS);
    },
    [dispatch, stopHold, tabId],
  );

  // Release anywhere ends the hold — the pointer is often well off the
  // button by the time it comes up.
  useEffect(() => {
    window.addEventListener("pointerup", stopHold);
    window.addEventListener("pointercancel", stopHold);
    return () => {
      window.removeEventListener("pointerup", stopHold);
      window.removeEventListener("pointercancel", stopHold);
      stopHold();
    };
  }, [stopHold]);

  /** Measured advance rate, bars/second, averaged over the last second of
   *  playback. Shown while playing next to the requested speed — if the two
   *  disagree, the chart is the limit, not the clock, and that is worth
   *  seeing rather than guessing at. */
  const [actualRate, setActualRate] = useState<number | null>(null);

  // Play loop: a time accumulator on requestAnimationFrame.
  //
  // THE LOOP OWNS THE CURSOR while it runs. It keeps its own bar index and
  // advances that, rather than reading the cursor back out of Redux each
  // frame. Reading it back is what kept every speed pinned to one bar:
  // React may not have re-rendered by the next frame, so the loop would
  // recompute from the SAME cursor, dispatch the SAME bar, and change
  // nothing — capping advancement at React's re-render cadence instead of
  // the requested rate, identically at 1x, 2x and 5x.
  //
  // The accumulator (rather than an interval at 1000/speed) is what keeps
  // the rate honest when a repaint costs more than one bar's worth of time:
  // elapsed TIME decides how many whole bars are owed, so at 50x on a 100ms
  // frame it advances 5 bars at once instead of falling behind.
  //
  // dt is clamped so a stall — or a backgrounded tab, where rAF stops firing
  // and playback pauses by itself — doesn't come back and jump hundreds of
  // bars. The clamp stays well above a realistic frame: clamping near the
  // frame time makes the accumulator under-count whenever a frame runs long,
  // dragging every speed towards the same floor.
  useEffect(() => {
    if (!playing) return;
    const bars = candlesRef.current;
    const last = bars.length - 1;
    if (last < 0) return;

    // Local, authoritative while playing.
    let idx = Math.max(replayEndIndex(bars, cursorRef.current ?? 0) - 1, 0);
    let raf = 0;
    let prev = performance.now();
    let carry = 0;
    // Rate measurement, so the readout reports what actually happened.
    let windowStart = prev;
    let windowBars = 0;
    const MAX_FRAME_MS = 1000;

    const tick = (now: number) => {
      const dt = Math.min(now - prev, MAX_FRAME_MS);
      prev = now;
      carry += (dt / 1000) * speedRef.current;
      const whole = Math.floor(carry);
      if (whole >= 1) {
        carry -= whole;
        // A manual step or jump while playing moves the cursor out from
        // under us; adopt it rather than yanking playback back.
        const external = Math.max(
          replayEndIndex(bars, cursorRef.current ?? 0) - 1,
          0,
        );
        if (external !== idx) idx = external;

        const next = Math.min(idx + whole, last);
        if (next !== idx) {
          windowBars += next - idx;
          idx = next;
          dispatch(setReplayCursor({ tabId, cursorTime: bars[idx].time }));
        }
        if (idx >= last) {
          // Caught up with the live edge.
          dispatch(setReplayPlaying({ tabId, playing: false }));
          return;
        }
      }
      if (now - windowStart >= 1000) {
        setActualRate((windowBars * 1000) / (now - windowStart));
        windowStart = now;
        windowBars = 0;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      setActualRate(null);
    };
  }, [playing, dispatch, tabId]);

  // Keyboard: ← / → step, Space plays. Ignored while typing so the drawing
  // dialogs and the trade-volume field keep their keys.
  //
  // Only the FOCUSED pane listens. In split view both panes are "active" and
  // mount their own bar, so two replays running side by side would otherwise
  // register two window-level listeners and one arrow key would step both.
  // Keyboard follows focus; the buttons stay per-pane, so clicking a pane's
  // controls always drives that pane.
  useEffect(() => {
    if (!hasKeyboard) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el?.isContentEditable) {
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        step(1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        step(-1);
      } else if (e.key === " ") {
        e.preventDefault();
        dispatch(setReplayPlaying({ tabId, playing: !playing }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dispatch, playing, step, tabId, hasKeyboard]);

  const jump = (to: "start" | "end") => {
    if (total === 0) return;
    dispatch(setReplayPlaying({ tabId, playing: false }));
    dispatch(
      setReplayCursor({
        tabId,
        cursorTime: candles[to === "start" ? 0 : total - 1].time,
      }),
    );
  };

  return (
    <div className={styles.bar} role="toolbar" aria-label="Chart replay">
      <span
        className={`${styles.replayDot} ${playing ? styles.playingDot : ""}`}
        aria-hidden
      />
      <div className={styles.status}>
        <span className={styles.stamp}>
          {cursorTime == null ? "—" : `${fmtLineLabel(cursorTime)} UTC`}
        </span>
        <span className={styles.position}>
          {total === 0
            ? "no bars loaded"
            : `bar ${index.toLocaleString()} / ${total.toLocaleString()}`}
          {actualRate != null ? (
            // Measured, not requested. When this sits well below the chosen
            // speed the chart is the bottleneck, not the clock.
            <span className={styles.rate}>
              {" · "}
              {actualRate >= 10 ? actualRate.toFixed(0) : actualRate.toFixed(1)}{" "}
              bars/s
            </span>
          ) : null}
        </span>
      </div>

      <div className={styles.divider} />

      <div className={styles.group}>
        <button
          type="button"
          className={styles.btn}
          onClick={() => jump("start")}
          disabled={atStart}
          title="Jump to the oldest loaded bar"
          aria-label="Jump to start"
        >
          <ToStartIcon />
        </button>
        {/* Step buttons act on pointerDOWN, not click, because holding them
            repeats — the press starts the run and the release ends it. */}
        <button
          type="button"
          className={styles.btn}
          onPointerDown={() => startHold(-1)}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          disabled={atStart}
          title="Step back one bar — hold to run back (←)"
          aria-label="Step back"
        >
          <StepBackIcon />
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPlay}`}
          onClick={() => dispatch(setReplayPlaying({ tabId, playing: !playing }))}
          disabled={atEnd && !playing}
          title={playing ? "Pause (Space)" : "Play (Space)"}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>
        <button
          type="button"
          className={styles.btn}
          onPointerDown={() => startHold(1)}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          disabled={atEnd}
          title="Step forward one bar — hold to run forward (→)"
          aria-label="Step forward"
        >
          <StepForwardIcon />
        </button>
        <button
          type="button"
          className={styles.btn}
          onClick={() => jump("end")}
          disabled={atEnd}
          title="Jump to the newest loaded bar"
          aria-label="Jump to end"
        >
          <ToEndIcon />
        </button>
      </div>

      <div className={styles.divider} />

      {/* Bars per second — not compressed wall-clock, so 1x means the same
          thing on every timeframe. */}
      <div className={styles.speeds} role="group" aria-label="Playback speed">
        {REPLAY_SPEEDS.map((s) => (
          <button
            key={s}
            type="button"
            className={`${styles.speed} ${s === speed ? styles.speedActive : ""}`}
            onClick={() => dispatch(setReplaySpeed({ tabId, speed: s as ReplaySpeed }))}
            title={`${s} bar${s === 1 ? "" : "s"} per second`}
            aria-pressed={s === speed}
          >
            {s}×
          </button>
        ))}
      </div>

      <div className={styles.divider} />

      <button
        type="button"
        className={`${styles.btn} ${styles.btnExit}`}
        onClick={() => dispatch(exitReplay({ tabId }))}
        title="Exit replay and return to live"
        aria-label="Exit replay"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

const EMPTY: never[] = [];
